import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';

export interface Schedule {
  id: string;
  day_names: string[];
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at: string;
}

export interface SchedulePayload {
  day_names: string[];
  start_time: string;
  end_time: string;
}

export interface SchedulesState {
  schedules: Schedule[];
  loading: {
    fetch: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
}

const initialState: SchedulesState = {
  schedules: [],
  loading: {
    fetch: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
};

export const fetchSchedulesList = createAsyncThunk<
  Schedule[],
  void,
  { rejectValue: string }
>('schedules/fetchSchedulesList', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('/schedules');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to fetch schedules'
    );
  }
});

export const createSchedule = createAsyncThunk<
  Schedule,
  SchedulePayload,
  { rejectValue: string }
>('schedules/createSchedule', async (payload, { rejectWithValue }) => {
  try {
    const response = await API.post('/schedules', payload);
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to create schedule'
    );
  }
});

export const updateSchedule = createAsyncThunk<
  Schedule,
  { id: string; data: SchedulePayload },
  { rejectValue: string }
>('schedules/updateSchedule', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await API.put(`/schedules/${id}`, data);
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to update schedule'
    );
  }
});

export const deleteSchedule = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('schedules/deleteSchedule', async (id, { rejectWithValue }) => {
  try {
    await API.delete(`/schedules/${id}`);
    return id;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to delete schedule'
    );
  }
});

const schedulesSlice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    clearSchedulesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedulesList.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchSchedulesList.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.schedules = action.payload;
      })
      .addCase(fetchSchedulesList.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload ?? 'Failed to fetch schedules';
      })
      .addCase(createSchedule.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        state.loading.create = false;
        if (action.payload) {
          state.schedules.push(action.payload);
        }
      })
      .addCase(createSchedule.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload ?? 'Failed to create schedule';
      })
      .addCase(updateSchedule.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.loading.update = false;
        if (action.payload) {
          const index = state.schedules.findIndex((s) => s.id === action.payload.id);
          if (index !== -1) {
            state.schedules[index] = action.payload;
          }
        }
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload ?? 'Failed to update schedule';
      })
      .addCase(deleteSchedule.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.schedules = state.schedules.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload ?? 'Failed to delete schedule';
      });
  },
});

export const { clearSchedulesError } = schedulesSlice.actions;
export default schedulesSlice.reducer;
